import React, { useEffect, useState } from 'react';
import Axios from 'axios';
import { Controller, useForm, useFieldArray } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import * as Routes from '@/modules/routes';
import SubmitModal from '@/components/ui/Modal/SubmitModal';
import Spinner from '@/components/common/Spinner';
import Select from '@/components/common/ReactSelect5';
import useSlideShowContext from '@/components/admin/SlideShow/stores/useSlideShowContext';
import Icon from '@/components/ui/Icon';
import I18n from '@/modules/i18n';
import { compareGlossaryTerms } from '@/utils/sortGlossaryTerms';
import { GlossaryTermModel, SlideObject } from '../../../types';

interface ReadingSelectModalProps {
  linkedVocab?: GlossaryTermModel[];
  modalManager: ModalManager;
  setLinkedVocab: React.Dispatch<React.SetStateAction<GlossaryTermModel[]>>;
  slideObject: SlideObject;
}

type VocabularyTerm = {
  image?: string;
  label: React.ReactNode;
  value: number;
};

type Term = {
  definition: string;
  id: number;
  s3_url?: string;
  term: string;
};

const VocabSelectModal = ({ linkedVocab, modalManager, setLinkedVocab, slideObject }: ReadingSelectModalProps) => {
  const [vocab, setVocab] = useState<VocabularyTerm[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const lessonId = useSlideShowContext(state => state.lessonId);
  const currentSlide = useSlideShowContext(state => state.currentSlide);
  const slideShowId = useSlideShowContext(state => state.slideShowId);

  const allowMultipleTerms = slideObject.modelType !== 'GlossaryTermCard';

  const getDefaultValues = () => (
    linkedVocab.length > 0 ? linkedVocab.map(lv => ({ value: lv.glossary_term_id })) : [{ value: null }]
  );

  const {
    formState: {
      isValid,
      isDirty,
    },
    handleSubmit,
    control,
    reset,
  } = useForm({
    defaultValues: {
      vocabularyField: getDefaultValues(),
    },
  });

  const {
    fields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'vocabularyField',
  });

  const { mutate: createGlossaryTermModels } = useMutation({
    mutationFn: (ids: number[]) => {
      const path = Routes.plato_api_bulk_create_glossary_term_models_path();

      const data = {
        glossary_term_model: {
          glossary_term_ids: ids,
          model_id: slideObject.id,
          model_type: 'SlideObject',
          position: currentSlide.position,
        },
      };

      return Axios.post(path, data);
    },
    onSuccess: (res) => {
      const glossaryTermModels = res.data.data;
      setLinkedVocab(glossaryTermModels as GlossaryTermModel[]);
      setSaving(false);
      modalManager.close();
    },
  });

  const formatVocab = (vocabTerms: Term[], slideShowIds: Set<number>) => (
    [...vocabTerms].sort((a, b) => compareGlossaryTerms(a.term, b.term, I18n.locale)).map(term => ({
      image: term.s3_url,
      label: (
        <div className="tw-flex tw-items-center">
          <Icon color={slideShowIds.has(term.id) ? 'green' : 'transparent'} icon="Checkmark" />
          <strong>{term.term}:</strong>
          &nbsp;
          <span dangerouslySetInnerHTML={{ __html: term.definition }} />
        </div>
      ),
      value: term.id,
    }))
  );

  const onSubmit = (data) => {
    setSaving(true);
    const ids = data.vocabularyField.map(id => id.value).filter(Number);
    createGlossaryTermModels(ids);
  };

  // Get vocabulary for Select Dropdowns
  useEffect(() => {
    if (!lessonId || !slideShowId) return;

    const lessonRoute = Routes.plato_api_get_glossary_terms_glossary_terms_path({ lesson_id: lessonId });
    const slideShowRoute = Routes.plato_api_get_glossary_terms_glossary_terms_path({ slide_show_id: slideShowId });

    Promise.all([
      Axios.get(lessonRoute),
      Axios.get(slideShowRoute),
    ])
      .then(([lessonRes, slideShowRes]) => {
        const lessonTerms: Term[] = (lessonRes.data.glossary_terms || []).filter(term => term.kind !== 'Academic');
        const slideShowTerms: Term[] = slideShowRes.data.glossary_terms || [];
        const slideShowIds = new Set(slideShowTerms.map(t => t.id));

        setVocab(formatVocab(lessonTerms, slideShowIds));
        setLoading(false);
      })
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  }, [lessonId, slideShowId]);

  // Set initial Select values
  useEffect(() => {
    if (linkedVocab.length > 0) {
      reset({
        vocabularyField: linkedVocab.map(lv => ({ value: lv.glossary_term_id })),
      });
    }
  }, [linkedVocab, reset]);

  return (
    <SubmitModal
      nearlyFullScreen
      close={modalManager.close}
      headerText="Link Vocabulary"
      isLoading={saving}
      isOpen={modalManager.isOpen}
      isValid={isValid && isDirty}
      submittingText="Saving..."
      onSubmit={handleSubmit(onSubmit)}
    >
      {loading ? <Spinner /> : (
        <div>
          <span className="tw-flex tw-items-center">
            Select the terms you want to include.
            &nbsp;
            Terms displayed with
            <Icon color="green" icon="Checkmark" />
            indicate the term has already been added to the slideshow.
          </span>
          {allowMultipleTerms && (<span>Terms will be displayed automatically in A-Z order.</span>)}
          <div className="tw-py-4">
            {fields.map((item, index) => (
              <div key={item.id} className="tw-flex tw-justify-between tw-items-center tw-py-2">
                <div className="tw-flex tw-items-center">
                  <div>Term {index + 1}</div>
                  {index > 0 && (
                    <button
                      className="fa fa-trash-o tw-text-red tw-border-0 tw-bg-transparent tw-w-4 tw-h-4
                        tw-cursor-pointer"
                      type="button"
                      onClick={() => remove(index)}
                    />
                  )}
                </div>
                <div className="tw-w-4/5">
                  <Controller
                    control={control}
                    name={`vocabularyField.${index}.value`}
                    render={({ field }) => (
                      <Select
                        isClearable
                        options={vocab}
                        placeholder="Select Vocabulary"
                        value={vocab.find(option => option.value === field.value) || null}
                        onChange={(option: VocabularyTerm) => field.onChange(option ? option.value : null)}
                      />
                    )}
                  />
                </div>
              </div>
            ))}
          </div>

        </div>
      )}
      {allowMultipleTerms && (
        <button
          className="btn btn--sm btn--outline-purple tw-w-28"
          type="button"
          onClick={() => append({ value: null })}
        >
          Add Choice
        </button>
      )}
    </SubmitModal>
  );
};

export default VocabSelectModal;
